import { FC } from "react";

import CargoDetails from "../tabs/cargoDetails";
import OriginCharge from "../tabs/originCharge";
import Information from "../tabs/information";
import PackingList from "../tabs/packingList";
import Insurance from "../tabs/insurance";
import Unloading from "../tabs/unloading";
import Delivery from "../tabs/delivery";
import Freight from "../tabs/freight";
import Customs from "../tabs/customs";
import TMS from "../tabs/tms";
import IncomeStatement from "../tabs/incomeStatement";
import { INCOME_STATEMENT_FEATURE } from "@pages/shippingView/config";

export const KEY_INFORMATION_VIEW_ROLES = [
  "Administration",
  "Sales",
  "Operations",
] as const;

export const KEY_INFORMATION_EDIT_ROLES = [
  "Administration",
  "Sales",
  "Operations",
] as const;

export const KEY_INFORMATION_FULL_ACCESS_ROLES = [
  "Administration",
  "Sales",
] as const;

export const CARGO_DETAILS_VIEW_ROLES = [
  "Administration",
  "Sales",
  "Operations",
  "Customer",
] as const;

export const CARGO_DETAILS_EDIT_ROLES = [
  "Administration",
  "Sales",
  "Operations",
] as const;

export const canEditCargoDetails = (role: string): boolean =>
  (CARGO_DETAILS_EDIT_ROLES as readonly string[]).includes(role);

export const INSURANCE_VIEW_ROLES = [
  "Administration",
  "Sales",
  "Operations",
] as const;

export const INSURANCE_EDIT_ROLES = [
  "Administration",
  "Sales",
  "Operations",
] as const;

export const canEditInsurance = (role: string): boolean =>
  (INSURANCE_EDIT_ROLES as readonly string[]).includes(role);

export const ORIGIN_CHARGE_VIEW_ROLES = [
  "Administration",
  "Sales",
  "Operations",
] as const;

export const ORIGIN_CHARGE_EDIT_ROLES = [
  "Administration",
  "Sales",
  "Operations",
] as const;

export const canEditOriginCharge = (role: string): boolean =>
  (ORIGIN_CHARGE_EDIT_ROLES as readonly string[]).includes(role);

export const FREIGHT_VIEW_ROLES = [
  "Administration",
  "Sales",
  "Operations",
] as const;

export const FREIGHT_EDIT_ROLES = [
  "Administration",
  "Sales",
  "Operations",
] as const;

export const canEditFreight = (role: string): boolean =>
  (FREIGHT_EDIT_ROLES as readonly string[]).includes(role);

export const UNLOADING_VIEW_ROLES = [
  "Administration",
  "Sales",
  "Operations",
  "Customer",
] as const;

const hasRole = (role: string, roles: readonly string[]): boolean =>
  roles.includes(role);

export const canViewUnloadingDetails = (role: string): boolean =>
  hasRole(role, ["Administration", "Sales", "Operations"]);

export const canEditUnloadingDetails = (role: string): boolean =>
  canViewUnloadingDetails(role);

export const canViewUnloadingWarehousing = (role: string): boolean =>
  hasRole(role, ["Administration", "Sales", "Operations"]);

export const canEditUnloadingWarehousing = (role: string): boolean =>
  hasRole(role, ["Administration", "Sales"]);

export const canViewUnloadingShifting = (role: string): boolean =>
  hasRole(role, ["Administration", "Sales", "Operations", "Customer"]);

export const canEditUnloadingShifting = (role: string): boolean =>
  hasRole(role, ["Administration", "Sales"]);

export const canViewUnloadingDepositary = (role: string): boolean =>
  hasRole(role, ["Administration", "Sales", "Operations", "Customer"]);

export const canEditUnloadingDepositary = (role: string): boolean =>
  hasRole(role, ["Administration", "Sales", "Operations"]);

export const canViewUnloadingPicking = (role: string): boolean =>
  hasRole(role, ["Administration", "Sales", "Operations", "Customer"]);

export const canEditUnloadingPicking = (role: string): boolean =>
  hasRole(role, ["Administration", "Sales"]);

export const canViewUnloadingFcr = (role: string): boolean =>
  hasRole(role, ["Administration", "Sales", "Operations", "Customer"]);

export const canEditUnloadingFcr = (role: string): boolean =>
  hasRole(role, ["Administration", "Sales"]);

export type UnloadingSubPanelPath =
  | "details"
  | "warehousing"
  | "deposit-warehouse"
  | "depositary-change"
  | "picking-service"
  | "fcr";

export const UNLOADING_SUB_PANELS: Array<{
  path: UnloadingSubPanelPath;
  canView: (role: string) => boolean;
  canEdit: (role: string) => boolean;
}> = [
  {
    path: "details",
    canView: canViewUnloadingDetails,
    canEdit: canEditUnloadingDetails,
  },
  {
    path: "warehousing",
    canView: canViewUnloadingWarehousing,
    canEdit: canEditUnloadingWarehousing,
  },
  {
    path: "deposit-warehouse",
    canView: canViewUnloadingShifting,
    canEdit: canEditUnloadingShifting,
  },
  {
    path: "depositary-change",
    canView: canViewUnloadingDepositary,
    canEdit: canEditUnloadingDepositary,
  },
  {
    path: "picking-service",
    canView: canViewUnloadingPicking,
    canEdit: canEditUnloadingPicking,
  },
  {
    path: "fcr",
    canView: canViewUnloadingFcr,
    canEdit: canEditUnloadingFcr,
  },
];

export const getVisibleUnloadingSubPanels = (role: string) =>
  UNLOADING_SUB_PANELS.filter((panel) => panel.canView(role));

export const DELIVERY_VIEW_ROLES = [
  "Administration",
  "Sales",
  "Operations",
  "Customer",
] as const;

export const DELIVERY_EDIT_ROLES = [
  "Administration",
  "Sales",
  "Operations",
] as const;

export const canViewDeliveryOrderList = (role: string): boolean =>
  hasRole(role, DELIVERY_VIEW_ROLES);

export const canEditDeliveryOrderList = (role: string): boolean =>
  hasRole(role, DELIVERY_EDIT_ROLES);

export const canViewDeliveryBalance = (role: string): boolean =>
  hasRole(role, DELIVERY_VIEW_ROLES);

export const canEditDeliveryBalance = (role: string): boolean =>
  hasRole(role, DELIVERY_EDIT_ROLES);

export const canViewDeliveryShifting = (role: string): boolean =>
  hasRole(role, DELIVERY_VIEW_ROLES);

export const canEditDeliveryShifting = (role: string): boolean =>
  hasRole(role, DELIVERY_EDIT_ROLES);

export const canViewDeliveryWhsList = (role: string): boolean =>
  hasRole(role, DELIVERY_EDIT_ROLES);

export const canEditDeliveryWhsList = (role: string): boolean =>
  hasRole(role, DELIVERY_EDIT_ROLES);

export type DeliverySubPanelPath =
  | "delivery-orders"
  | "balance-volume"
  | "deposit-warehouse"
  | "whs-delivery-orders";

export const DELIVERY_SUB_PANELS: Array<{
  path: DeliverySubPanelPath;
  label: string;
  canView: (role: string) => boolean;
  canEdit: (role: string) => boolean;
}> = [
  {
    path: "delivery-orders",
    label: "Delivery Order List",
    canView: canViewDeliveryOrderList,
    canEdit: canEditDeliveryOrderList,
  },
  {
    path: "balance-volume",
    label: "Shipping Case Balance Volume",
    canView: canViewDeliveryBalance,
    canEdit: canEditDeliveryBalance,
  },
  {
    path: "deposit-warehouse",
    label: "Deposit / Warehouse Shifting",
    canView: canViewDeliveryShifting,
    canEdit: canEditDeliveryShifting,
  },
  {
    path: "whs-delivery-orders",
    label: "Delivery Order List (WHS)",
    canView: canViewDeliveryWhsList,
    canEdit: canEditDeliveryWhsList,
  },
];

export const getVisibleDeliverySubPanels = (role: string) =>
  DELIVERY_SUB_PANELS.filter((panel) => panel.canView(role));

export const CUSTOMS_VIEW_ROLES = [
  "Administration",
  "Sales",
  "Operations",
  "Customer",
] as const;

export const CUSTOMS_EDIT_ROLES = [
  "Administration",
  "Sales",
  "Operations",
] as const;

export const canEditCustoms = (role: string): boolean =>
  hasRole(role, CUSTOMS_EDIT_ROLES);

export const canViewCustoms = (role: string): boolean =>
  hasRole(role, CUSTOMS_VIEW_ROLES);

export const TMS_VIEW_ROLES = [
  "Administration",
  "Sales",
  "Operations",
  "WMS+Operations",
  "Supplier",
  "Supplier Terminal",
  "Supplier Warehouse",
] as const;

export const TMS_FULL_ACCESS_ROLES = [
  "Administration",
  "Sales",
  "Operations",
  "WMS+Operations",
] as const;

export const canViewTms = (role: string): boolean =>
  hasRole(role, TMS_VIEW_ROLES);

export const canFullAccessTms = (role: string): boolean =>
  hasRole(role, TMS_FULL_ACCESS_ROLES);

export const isSupplierWarehouseTmsRole = (role: string): boolean =>
  role === "Supplier Warehouse";

export const isSupplierTerminalTmsRole = (role: string): boolean =>
  role === "Supplier Terminal";

export const canInlineEditTmsGrid = (role: string): boolean =>
  canFullAccessTms(role) || isSupplierWarehouseTmsRole(role);

export const canOpenTmsLoadingOrderDetail = (role: string): boolean =>
  canViewTms(role);

export const canOpenTmsWhsLoadingOrderDetail = (role: string): boolean =>
  canViewTms(role);

export const isTmsLoadingOrderDetailViewOnly = (
  role: string,
  status: string,
): boolean => {
  if (["EXECUTED", "CANCELED"].includes(status)) return true;
  if (canFullAccessTms(role)) return false;
  if (isSupplierWarehouseTmsRole(role)) {
    return !["READY_TO_PICK", "PICKING"].includes(status);
  }
  return true;
};

export const isTmsWhsLoadingOrderDetailViewOnly = (
  role: string,
  status: string,
): boolean => {
  if (["EXECUTED", "CANCELED"].includes(status)) return true;
  if (canFullAccessTms(role)) return false;
  if (isSupplierWarehouseTmsRole(role)) {
    return !["PENDING", "PICKING"].includes(status);
  }
  return true;
};

export const canCancelTmsLoadingOrder = (role: string): boolean =>
  canFullAccessTms(role);

export const canDownloadTmsTransportDocuments = (role: string): boolean =>
  canFullAccessTms(role);

export const canReviseTmsLoadingOrder = (role: string): boolean =>
  canFullAccessTms(role);

export const canIssueTmsDdt = (role: string): boolean => canFullAccessTms(role);

export const PACKING_LIST_VIEW_ROLES = [
  "Administration",
  "Sales",
  "Operations",
  "WMS+Operations",
  "Customer",
  "Supplier Terminal",
] as const;

export const PACKING_LIST_EDIT_ROLES = [
  "Administration",
  "Sales",
  "Operations",
  "WMS+Operations",
] as const;

export const PACKING_LIST_MODIFY_DELIVERED_ROLES = [
  "Administration",
  "Sales",
  "Operations",
] as const;

export const canViewPackingList = (role: string): boolean =>
  hasRole(role, PACKING_LIST_VIEW_ROLES);

export const canEditPackingList = (role: string): boolean =>
  hasRole(role, PACKING_LIST_EDIT_ROLES);

export const canModifyDeliveredPackingList = (role: string): boolean =>
  hasRole(role, PACKING_LIST_MODIFY_DELIVERED_ROLES);

type TabItem = {
  label: string;
  component: FC<any>;
  button?: string;
  required: boolean;
  slug: string;
  roles: string[];
};

export const tabComponentMap: Record<string, TabItem> = {
  keyInfomation: {
    label: "Key Information",
    component: Information,
    required: true,
    slug: "key-information",
    roles: [...KEY_INFORMATION_VIEW_ROLES],
  },
  cargoDetails: {
    label: "Cargo Details + Tracking",
    component: CargoDetails,
    button: "Cargo Details + Tracking",
    required: true,
    slug: "cargo-details",
    roles: [...CARGO_DETAILS_VIEW_ROLES],
  },
  packingList: {
    label: "Packing List",
    component: PackingList,
    button: "Packing List",
    required: true,
    slug: "packing-list",
    roles: [...PACKING_LIST_VIEW_ROLES],
  },
  insurance: {
    label: "Insurance",
    component: Insurance,
    button: "Insurance",
    required: true,
    slug: "insurance",
    roles: [...INSURANCE_VIEW_ROLES],
  },
  originCharge: {
    label: "Origin Charge",
    component: OriginCharge,
    button: "Origin Charge",
    required: true,
    slug: "origin-charge",
    roles: [...ORIGIN_CHARGE_VIEW_ROLES],
  },
  freight: {
    label: "Freight",
    component: Freight,
    button: "Freight",
    required: true,
    slug: "freight",
    roles: [...FREIGHT_VIEW_ROLES],
  },
  delivery: {
    label: "Delivery",
    component: Delivery,
    button: "Delivery",
    required: true,
    slug: "delivery",
    roles: [...DELIVERY_VIEW_ROLES],
  },
  customs: {
    label: "Customs",
    component: Customs,
    button: "Customs",
    required: true,
    slug: "customs",
    roles: [...CUSTOMS_VIEW_ROLES],
  },
  unloading: {
    label: "Unloading",
    component: Unloading,
    button: "Unloading",
    required: true,
    slug: "unloading",
    roles: [...UNLOADING_VIEW_ROLES],
  },
  tms: {
    label: "TMS",
    component: TMS,
    button: "TMS",
    required: true,
    slug: "tms",
    roles: [...TMS_VIEW_ROLES],
  },
};

if (INCOME_STATEMENT_FEATURE) {
  tabComponentMap["incomeStatement"] = {
    label: "Income Statement",
    component: IncomeStatement,
    button: "Income Statement",
    required: true,
    slug: "income-statement",
    roles: ["Administration", "Sales", "Operations", "WMS+Operations"],
  };
}

export const routeToTabKeyMap: Record<string, string> = {
  ORIGIN_CHARGE: "originCharge",
  FREIGHT: "freight",
  DELIVERY: "delivery",
  INSURANCE: "insurance",
  UNLOADING: "unloading",
  WAREHOUSING: "unloading",
  CUSTOMS: "customs",
};

const TABLET_SHIPPING_TABS = ["tms"] as const;

export const getVisibleShippingTabSlugs = (
  role: string,
  availableActivities: Record<string, unknown>,
  isTablet: boolean,
): string[] => {
  const dynamicTabKeys = new Set<string>();

  Object.keys(availableActivities).forEach((key) =>
    dynamicTabKeys.add(routeToTabKeyMap[key]),
  );

  return Object.entries(tabComponentMap).reduce<string[]>(
    (acc, [key, { slug, roles, required }]) => {
      if (
        isTablet &&
        !TABLET_SHIPPING_TABS.includes(
          key as (typeof TABLET_SHIPPING_TABS)[number],
        )
      ) {
        return acc;
      }

      if ((required || dynamicTabKeys.has(key)) && roles.includes(role)) {
        acc.push(slug);
      }

      return acc;
    },
    [],
  );
};
